import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';

import { finalize } from 'rxjs/operators';
import { ApiError, Auth, AuthService, ConfigService } from '../core';
import { ToastService } from '../domains/shared/components/toast/services/toast.service';

interface ILoginForm {
  email: string;
  senha: string;
  stayConnected: boolean;
}

@Component({
  selector: 'sgt-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit {
  loginForm: FormGroup;
  showPassword = false;
  isLoading = false;
  private returnUrl: string | null = null;

  constructor(
    private fb: FormBuilder,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private authService: AuthService,
    private configService: ConfigService,
    private toastService: ToastService
  ) {}

  ngOnInit() {
    this.loginForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      senha: ['', [Validators.required, Validators.minLength(3)]],
      stayConnected: [false]
    });

    this.returnUrl =
      this.activatedRoute.snapshot.queryParams['returnUrl'] ?? null;
  }

  get formValue(): ILoginForm {
    return this.loginForm.getRawValue();
  }

  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  login(): void {
    this.isLoading = true;

    if (this.loginForm.invalid) {
      this.loginForm.markAllAsTouched();
      this.isLoading = false;
      return;
    }

    const auth = new Auth();
    const { email, senha, stayConnected } = this.formValue;
    auth.email = email;
    auth.senha = senha;

    this.authService
      .login(auth, stayConnected)
      .subscribe({
        next: () => {
          this.configService
            .load()
            .pipe(
              finalize(() => {
                this.isLoading = false;
              }
            ))
            .subscribe({
              next: () => this.redirectAccess(),
              error: () => this.redirectAccess(),
            });
        },
        error: (error: ApiError) => {
          this.isLoading = false;
          this.toastService.error(
            error?.cause ||
              'Ocorreu um erro inesperado ao tentar realizar o login. Por favor, tente novamente.',
            {
              duration: 8000,
              title: error?.message || 'Erro ao realizar login',
            }
          );
        },
      });
  }

  private redirectAccess(): void {
    if (this.returnUrl && this.isValidReturnUrl(this.returnUrl)) {
      this.router.navigateByUrl(this.returnUrl);
      return;
    }

    this.router.navigate(this.authService.getDefaultRoute(), {
      relativeTo: this.activatedRoute
    });
  }

  private isValidReturnUrl(url: string): boolean {
    return url.startsWith('/') && !url.startsWith('//');
  }
}
